import { Line, LineChart, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import type { MonthSpend, YearlyCategoryBudget } from '@/api/budget'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { useIsDarkMode } from '@/hooks/useIsDarkMode'
import { formatShortMonth } from '@/lib/formatDate'
import { fromPaise, toPaise } from '@/lib/splitMath'

// Recharts SVG props take plain hex: these mirror the --fl-hero-mint-* / --fl-error / --fl-text-3 tokens.
const COLORS = {
  light: { line: '#5aa97f', now: '#12372a', over: '#b01515', limit: '#888888', axis: '#555555' },
  dark: { line: '#6fbf92', now: '#e6f5ec', over: '#e88080', limit: '#706860', axis: '#a09890' },
}

function runningTotals(months: MonthSpend[], periodMonths: Set<string>) {
  let runningPaise = 0
  return months.map((m) => {
    runningPaise += toPaise(m.spent)
    const total = fromPaise(runningPaise)
    return { month: m.month, total, now: periodMonths.has(m.month) ? total : null }
  })
}

/**
 * The year's running total for one yearly budget against its limit, with the selected period's
 * months drawn heavier.
 */
export function YearlyBurnChart({
  budget,
  periodMonths,
}: {
  budget: YearlyCategoryBudget
  /** yyyy-MM months of the selected period. */
  periodMonths: Set<string>
}) {
  const { formatCompact } = useFormatCurrency()
  const colors = COLORS[useIsDarkMode() ? 'dark' : 'light']
  const points = runningTotals(budget.months, periodMonths)
  // The period's line starts from the month before it, so its step reads as its own.
  const firstNow = points.findIndex((p) => p.now != null)
  if (firstNow > 0) points[firstNow - 1].now = points[firstNow - 1].total
  const nowColor = budget.isOverBudget ? colors.over : colors.now

  return (
    <div className="h-[110px] w-full" data-testid="yearly-burn-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 14, right: 8, bottom: 0, left: 8 }}>
          <XAxis
            dataKey="month"
            tickFormatter={formatShortMonth}
            tick={{ fill: colors.axis, fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis hide domain={[0, (max: number) => Math.max(max, budget.limit)]} />
          <ReferenceLine
            y={budget.limit}
            stroke={colors.limit}
            strokeDasharray="4 3"
            label={{
              value: `Budget ${formatCompact(budget.limit)}`,
              position: 'insideTopRight',
              fill: colors.axis,
              fontSize: 10,
            }}
          />
          <Line
            type="linear"
            dataKey="total"
            stroke={colors.line}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="linear"
            dataKey="now"
            stroke={nowColor}
            strokeWidth={3.5}
            dot={false}
            activeDot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
